import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import PaymentReturn from "./pages/PaymentReturn";
import Legal from "./pages/Legal";
import SignIn from "./pages/SignIn";
import ScamGuide from "./pages/ScamGuide";
import About from "./pages/About";
import Unsubscribe from "./pages/Unsubscribe";
import AdminScamReports from "./pages/AdminScamReports";
import CookieBanner from "./components/CookieBanner";
import { KeshWidget } from "./components/KeshWidget";
import { Loader2 } from "lucide-react";
import { useTheme } from "./components/ThemeToggle";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas">
      <Loader2 className="w-8 h-8 text-brand animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Process OAuth redirect FIRST (synchronously during render) to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/payment/success" element={<PaymentReturn />} />
      <Route path="/payment/cancel" element={<PaymentReturn cancelled />} />
      <Route path="/privacy" element={<Legal type="privacy" />} />
      <Route path="/data-retention" element={<Legal type="retention" />} />
      <Route path="/pdpa" element={<Legal type="pdpa" />} />
      <Route path="/terms" element={<Legal type="terms" />} />
      <Route path="/scam-guide" element={<ScamGuide />} />
      <Route path="/about" element={<About />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route
        path="/dashboard/:view?/:appId?"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/scam-reports"
        element={
          <ProtectedRoute>
            <AdminScamReports />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { dark } = useTheme();
  return <Toaster position="top-right" offset={{ top: 76, right: 24 }} mobileOffset={{ top: 72, left: 16, right: 16 }} theme={dark ? "dark" : "light"} richColors />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemedToaster />
          <AppRouter />
          <CookieBanner />
          <KeshWidget />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
